/**
 * maturity.ts — queue #643 §UAT-2026-07-07-R643 §R643-4.
 *
 * `deriveMaturity(node)` 순수 함수 — 카탈로그 노드의 성숙도 등급(GA/BETA/DEPRECATED)을
 * **git 과 독립적으로** 파생한다. 입력은 이미 커밋된 `module-versions.json`(#637 SSOT · 선언
 * 버전)과 `ModuleNode.supersededBy`(#617 BE 필드)뿐 — git 이력(`module-maturity.json`)은
 * 등급 산정에 관여하지 않는다(활발도 보조표시 전용, §R643-4). R640 `facet-predicates.ts`
 * 패턴(순수 함수 + 공유 헬퍼)을 그대로 답습한다.
 *
 * 판정 규칙(우선순위 순):
 *   1. DEPRECATED — node.supersededBy != null (★ `!== null` 금지 — JSON 상 생략 가능한
 *      optional 필드라 undefined 도 함께 걸러야 한다).
 *   2. BETA       — 선언 버전이 정확히 1.0.0(-SNAPSHOT 제거 후) — 초기 릴리스·미반복.
 *   3. GA         — 선언 버전이 1.0.0 보다 큼(minor/patch 가 최소 1회 반복됨).
 *   선언 버전 부재(module-versions.json 미등재 + node.version 도 없음)는 과대 성숙 방지를
 *   위해 BETA 로 보수 판정한다.
 */
import type { ModuleNode, ModuleVersions } from '../types/showcase';

export type MaturityGrade = 'GA' | 'BETA' | 'DEPRECATED';

/**
 * §R617 C032 넘버링 충돌 가드 — common-capabilities.md 상 C032 의 후계 코드 "G015" 는
 * 쇼케이스 자체 G-넘버링 체계(G001~G015, 기존 G015=WebSocket)와 값이 충돌한다. `SupersedeNotice`
 * (CommonShowcasePage.tsx)와 `MaturityBadge`(§R643-6 결합 표시)가 공유하는 단일 진실 원천 —
 * 두 곳에 중복 정의하면 향후 드리프트 위험이 있어 이 파일에 한 번만 정의한다.
 */
export const SUPERSEDE_JUMP_DISABLED_CODES = new Set(['C032']);

/** "1.2.0-SNAPSHOT" → "1.2.0" (SNAPSHOT 접미 제거 · 순수 버전 비교용). */
function stripSnapshot(version: string): string {
  return version.replace(/-SNAPSHOT$/i, '').trim();
}

/** "1.2.0" → [1,2,0]. 파싱 불가(이례적 형식)면 null. */
function parseSemver(version: string): [number, number, number] | null {
  const m = stripSnapshot(version).match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** a-b 부호로 비교(major→minor→patch 순). */
function compareSemver(a: [number, number, number], b: [number, number, number]): number {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

/**
 * 선언 버전 조회 — module-versions.json.modules[artifact] 우선, 없으면 node.version
 * fallback(§R643-4 방어). 그마저 없으면 null(호출부가 BETA 로 보수 판정).
 */
function resolveDeclaredVersion(node: ModuleNode, moduleVersions: ModuleVersions): string | null {
  const declared = moduleVersions.modules[node.artifact];
  if (declared) return declared;
  return node.version || null;
}

export function deriveMaturity(node: ModuleNode, moduleVersions: ModuleVersions): MaturityGrade {
  if (node.supersededBy != null) return 'DEPRECATED';

  const version = resolveDeclaredVersion(node, moduleVersions);
  if (!version) return 'BETA'; // 버전 정보 부재 — 과대 성숙 방지(보수적 BETA)

  const parsed = parseSemver(version);
  if (!parsed) return 'BETA'; // 이례적 버전 형식 — 파싱 불가 시 보수적 BETA

  const cmp = compareSemver(parsed, [1, 0, 0]);
  if (cmp === 0) return 'BETA'; // 정확히 1.0.0 — 초기 릴리스·미반복
  if (cmp > 0) return 'GA'; // 1.0.0 초과 — minor/patch 최소 1회 반복됨
  return 'BETA'; // 1.0.0 미만(실측엔 없는 이례적 케이스) — 보수적 BETA
}
